import { Asset } from "@models/asset/asset.model";
import { FormPageService } from "~/app/base/formPage.service";
import { HttpClient } from "@angular/common/http";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Lexicon } from "@models/lexicon/lexicon.model";
import { UploadAuthorization } from "@models/shared/upload";
import {
  Project,
  ProjectsDto,
  ProjectStatus,
  VoiceType,
} from "@models/textToSpeech/project.model";
import {
  CreateAndStartProduction,
  KeyValuePair,
} from "@models/textToSpeech/textToSpeech.model";
import { Status, UploadAuthorizationBody } from "@models/upload/file.model";
import { User } from "@models/user/user.model";
import { TranslateService } from "@ngx-translate/core";
import { filter, forkJoin, of, tap } from "rxjs";
import { DurationFormatterService } from "~/app/shared/duration-formatter.service";
import { FileService } from "~/app/shared/file.service";
import {
  MultiPartUploadHandler,
  Notification,
} from "~/app/shared/multi-part-upload-handler";
import { ProjectService } from "~/app/textToSpeech/project.service";
import { TextToSpeechService } from "~/app/textToSpeech/textToSpeech.service";
import { UserHelperService } from "~/app/shared/userHelper.service";
import { noWhitespaceValidator, checkFrameRate } from "~/app/shared/validators";
import { environment } from "~/environments/environment";
import { newUuid } from "~/utils/newUuid";
import {
  getTimeInSecondsFromFrameRate,
  getTimeInSecondsFromMillis,
} from "~/utils/project";
import { DiscardProjectConfirmationComponent } from "../discard-project-confirmation/discard-project-confirmation.component";
import { ManagementService } from "~/app/management/management.service";
import { encodeS3URI } from "~/utils/encodeS3URI";
import { ProjectTemplate } from "@models/template/template.model";
import { UploadFileComponent } from "~/app/shared/components/upload-file/upload-file.component";
import { ScriptWritingComponent } from "../script-writing/script-writing.component";
import { CatalogAssetSelectionService } from "~/app/shared/catalog-assets-selection.service";
import { AssetService } from "~/app/asset/asset.service";
import { ConnectWebSocketService } from "../../connectWebSocket.service";

export interface FileUploadContext {
  file: File;
  assetId: string; // unique projectName
  upload: {
    remainingTime: number; // estimated remaining time in millis before upload is finished
    status: string; // as red or orange or green
    message: string; // progress (as %) or checksum evaluation, or (network) error ...
    progress: number;
  };
  multiPartUploadHandler?: MultiPartUploadHandler;
  authorization?: UploadAuthorization;
  projectName: string;
  isValidated?: boolean;
  isEdited?: boolean; // edit project scenario
  isDeleted?: boolean; // for undo
  lufs?: string;
  retakesGenerationStoredInDb?: boolean;
}

export interface ProjectDataBackup {
  voice: string;
  lexicon: string;
  speed: number;
  automatedSpeedRate: number;
  frameRate: number;
  isAutomatedSpeed: boolean;
}

@Component({
  selector: "app-create-project",
  templateUrl: "./create-project.component.html",
  styleUrls: ["./create-project.component.scss"],
})
export class CreateProjectComponent implements OnInit {
  private readonly documentUploadExtensions = [
    ".pdf",
    ".docx",
    ".ttml",
    ,
    ".xml",
  ];

  @ViewChild(UploadFileComponent, { static: false })
  private uploadFileComponent: UploadFileComponent;

  @ViewChild(ScriptWritingComponent, { static: false })
  private scriptWritingComponent: ScriptWritingComponent;

  @ViewChild("stepper", { static: true })
  private stepper: MatStepper;

  @Input() project: Project;
  @Input() isNotEditable: boolean;
  @Input() errorMessage: string;
  @Input() pageId: number;

  public uploadCompleted = false;
  public status = Status;
  public fileUploadContexts: FileUploadContext[] = [];
  public filesAllowedWarning = false;
  public languages: KeyValuePair[];
  public regions: KeyValuePair[];
  public lufs: KeyValuePair[];
  public voices: any[];
  public voiceStyle: string[];
  public voiceProviders: string[] = [];
  public userName: string;
  public disableControl = false;
  public lexicons: Lexicon[];
  public usersToNotify: User[];
  public cancelledAssetIds: string[];

  private currentAudio: HTMLAudioElement | null = null;
  public playingVoiceId: string | null = null;

  public spinner = false;
  public variableSpeedWarning = false;
  public projectReview: any;
  public formGroup: UntypedFormGroup;
  public durationTextMask = {
    mask: [/\d/, /\d/, ":", /\d/, /\d/, ":", /\d/, /\d/, ":", /\d/, /\d/],
  };

  public projectId: number;
  public pageTitle: string;
  public mainButtonName = "createProject.continueButton";
  public mainButtonText = "createProject.continueButton";
  public projectFilesAttached: any[] = [];

  public uploadSingleFile = false;
  public recentlyDeletedFile: FileUploadContext;
  private userEnteredProjectName = "";
  public silenceWarningMessage: string;
  public nonDropFileWarningMessage: string;
  public projectTemplate: ProjectTemplate[];
  public authorizedExtensions = "";
  private backedProjectTemplate: ProjectTemplate[] = [];
  private selectedPresetTemplate: ProjectTemplate;
  private projectDataBackup: ProjectDataBackup;
  private allowXmlOnly = false;
  public showProjectSection = true;
  public selectedAsset: Asset[];
  public isProduction = environment.production;
  public defaultLuf = environment.project.defaultLuf;
  public retakesGenerationStoredInDb = false;
  private isEdit: boolean;
  public voicerReadingSpeed = "createProject.speedRange";
  public canUploadDocumentFiles = false;

  @ViewChild("audioSource", { static: true }) audioSource: ElementRef;
  microphoneList: any[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private projectService: ProjectService,
    protected fileService: FileService,
    private translateService: TranslateService,
    private durationFormatterService: DurationFormatterService,
    protected http: HttpClient,
    private textToSpeechService: TextToSpeechService,
    private managementService: ManagementService,
    private userHelperService: UserHelperService,
    private router: Router,
    private dialog: MatDialog,
    private cdk: ChangeDetectorRef,
    private formPageService: FormPageService,
    private activatedRoute: ActivatedRoute,
    private assetSelectionService: CatalogAssetSelectionService,
    private assetService: AssetService,
    private connectWebSocketService: ConnectWebSocketService
  ) {
    this.userHelperService.sharedUserName.subscribe(
      (username) => (this.userName = username)
    );
    this.userHelperService.sharedEmotionTransferActivated.subscribe(
      (isEmotionTransferActivated) =>
        (this.canUploadDocumentFiles = isEmotionTransferActivated)
    );
    this.lufs = environment.project.lufs;
    this.languages = environment.project.languages.list;
    const regionKeyValuePairMaster = environment.project.regions.list.find(
      (element) => {
        return (
          element.key.toLowerCase() ===
          environment.project.languages.default.toLowerCase()
        );
      }
    );

    if (regionKeyValuePairMaster) {
      this.regions = regionKeyValuePairMaster.value;
    }
  }

  @HostListener("window:unload", ["$event"])
  unload($event: Event) {
    this.confirmCancelUpload();
  }

  async ngOnInit() {
    window.scroll(0, 0);
    this.setupFormControls();
    this.formGroupChangeDetection();
    this.handleAutoRestoreLogic();
    this.handleProjectEdit();
    this.getAllTemplates();
    this.getUsers();

    // Listen to 'focus' event on the projectName FormControl
    this.formGroup.controls["projectName"].valueChanges.subscribe(
      (value: string) => {
        this.userEnteredProjectName = value;
      }
    );

    this.handleVoicerSettingChange();
    this.handleFormGroupValueChanges();
    this.handleSocket();
  }
  handleSocket() {
    this.connectWebSocketService.connectToProjectSocket();
    this.connectWebSocketService.connectToAssetsSocket();

    this.connectWebSocketService.socketMessages.subscribe((message: string) => {
      const parsedMessage = this.parseMessage(message);
      if (!parsedMessage) {
        return;
      }

      if (parsedMessage.lufs) {
        const foundFileUploadContext = this.fileUploadContexts.find(
          (fileUploadContext) =>
            fileUploadContext.file &&
            fileUploadContext.file.name === parsedMessage.fileName
        );
        if (foundFileUploadContext) {
          foundFileUploadContext.lufs = parsedMessage.lufs;

          if (this.areFilesAllUploaded) {
            this.processUploadCompleted();
          }

          setTimeout(() => {
            this.cdk.detectChanges();
          }, 0);
        }
        return;
      }
      this.processWebSocketMessage(parsedMessage);
    });
  }

  private parseMessage(message: string): any {
    try {
      return JSON.parse(message);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
      return null;
    }
  }

  private processWebSocketMessage(messageObj: any): void {
    this.fileUploadContexts.forEach((file: any) => {
      if (file.assetId === messageObj.uniqueProjectName) {
        file.retakesGenerationStoredInDb =
          messageObj.retakesGenerationStoredInDb;
        if (messageObj.retakesGenerationStoredInDb) {
          file.upload.message = this.translateService.instant(
            "createProject.upload.completed"
          );
          file.upload.status = Status.UploadSuccess;
        }
      }
    });
  }

  private handleAutoRestoreLogic(): void {
    this.formPageService
      .shouldAutoRestore(this.activatedRoute.queryParams)
      .pipe(
        filter((isAutoRestore: boolean) => isAutoRestore),
        tap(() => {
          const assets =
            this.assetSelectionService.getSelection()?.assets || [];
          const videoAsset = assets?.find(
            (asset) => asset.assetType === "video"
          );
          const audioAsset = assets?.find(
            (asset) => asset.assetType === "audio"
          );

          if (!videoAsset && this.project) {
            this.project.videoAssetId = null;
          }
          if (!audioAsset && this.project) {
            this.project.audioAssetId = null;
            this.project.stereoFilePath = null;
          }
          const draftData = sessionStorage.getItem("draftProjectData");
          const parsedDraftData = draftData ? JSON.parse(draftData) : null;

          if (
            parsedDraftData?.hasOwnProperty("stereoFilePath") &&
            this.project
          ) {
            this.project.stereoFilePath = parsedDraftData.stereoFilePath;
          }
          if (parsedDraftData?.hasOwnProperty("videoAssetId") && this.project) {
            this.project.videoAssetId = parsedDraftData.videoAssetId;
          }
          if (parsedDraftData?.hasOwnProperty("audioAssetId") && this.project) {
            this.project.audioAssetId = parsedDraftData.audioAssetId;
          }
          if (parsedDraftData && Object.keys(parsedDraftData).length) {
            this.formGroup.patchValue(parsedDraftData, { emitEvent: false });
          }
          if (!this.formGroup.get("voiceSettingType").value) {
            this.formGroup.patchValue(
              { voiceSettingType: VoiceType.Synthetic },
              { emitEvent: false }
            );
          }
          this.applyVoiceSettingSideEffects(
            this.formGroup.get("voiceSettingType").value
          );
          this.formPageService.setAutoRestore(false);
          this.projectService.getFileUploadContexts().subscribe((data) => {
            this.fileUploadContexts = data;
          });
          if (assets.length) {
            this.selectedAsset = assets;

            this.formGroup
              .get("startTimeCode")
              .patchValue(
                videoAsset?.startTimeCode
                  ? videoAsset?.startTimeCode.replace(";", ":")
                  : "00:00:00:00"
              );
            this.formGroup.get("frameRate").patchValue(videoAsset?.frameRate);
            if (this.project) {
              this.project.frameRate = Number(videoAsset?.frameRate)
                ? Number(videoAsset?.frameRate)
                : this.project.frameRate;
              this.project.startTimeCode = videoAsset?.startTimeCode
                ? videoAsset?.startTimeCode.replace(";", ":")
                : this.project.startTimeCode;
            }
            if (this.isProjectCreationValid() || this.validateProjectFiles()) {
              // trying to create project with wav and ttml and more than one
              alert(
                this.translateService.instant("createProject.upload.mixUpload")
              );
              return;
            }
          }
          if (
            parsedDraftData?.targetTranslationLanguage == null &&
            this.project?.targetTranslationLanguage != null
          ) {
            this.formGroup
              .get("targetTranslationLanguage")
              .patchValue(this.project.targetTranslationLanguage, {
                emitEvent: false,
              });
          }
          this.onLanguageSelectionChange(this.latestLanguageValue, true);
          if (!this.projectId) {
            this.getVoicesAndLexicons(this.latestLanguageRegionValue, false);
          }
        })
      )
      .subscribe();
  }

  private handleFormGroupValueChanges(): void {
    this.formGroup.valueChanges.subscribe((value) => {
      value.selectedTabIndex = !!this.projectId ? 1 : 0;
      sessionStorage.setItem("draftProjectData", JSON.stringify(value));
    });
  }

  private setSelectedUsersToNotify(): void {
    const selectedUserIds = this.formGroup.get("selectedUsersToNotifyList")
      ? (this.formGroup.get("selectedUsersToNotifyList").value as number[])
      : null;
    if (selectedUserIds) {
      this.usersToNotify.forEach(
        (user) => (user.isSelected = selectedUserIds.includes(user.id))
      );
    }
  }

  get latestLanguageValue(): string {
    return this.formGroup.get("language").value;
  }

  get latestLanguageRegionValue(): string {
    return `${this.formGroup.get("language").value}-${
      this.formGroup.get("region").value
    }`;
  }

  private getUsers(): void {
    this.managementService
      .getUsersToNotify()
      .subscribe((users) => (this.usersToNotify = users))
      .add(() => {
        if (this.projectId) {
          const existingUsersToNotify = this.project.usersToNotify
            ? this.project.usersToNotify?.split(",").map((i: any) => Number(i))
            : [];
          existingUsersToNotify.forEach((userId: any) => {
            this.usersToNotify
              .filter((e) => e.id === userId)
              .map((x) => (x.isSelected = true));
          });
          this.setSelectedUsersToNotify();
          if (this.pageId !== undefined) {
            this.goToScriptWriting();
          }
        }
      });
  }

  private getVoicesAndLexicons(
    languageRegion: string,
    isFromPreset: boolean,
    lexiconId?: number
  ): void {
    this.voices = [];
    this.lexicons = [];
    const voiceObs = this.textToSpeechService.getVoicesByLanguageAndRegion(
      languageRegion,
      this.formGroup.get("voiceProvider")?.value
    );
    const lexiconObs =
      this.projectService.getLexiconsByLanguageAndRegion(languageRegion);
    forkJoin([voiceObs, lexiconObs])
      .subscribe(([voices, lexicons]) => {
        this.voices = voices;
        this.lexicons = lexicons;
        this.extractVoiceProviders(voices);
      })
      .add(() => {
        if (isFromPreset && lexiconId) {
          const isLexiconFound = this.lexicons.find(
            (lexicon) => lexicon.id === lexiconId
          );
          this.formGroup.patchValue({
            lexicon:
              this.formGroup.controls["lexicon"].pristine ||
              (this.formGroup.controls["lexicon"].dirty &&
                this.formGroup.controls["lexicon"].untouched)
                ? isLexiconFound
                  ? isLexiconFound.fileUri
                  : " "
                : this.formGroup.get("lexicon").value,
          });
        }
        this.spinner = false;
      });
  }

  private applyVoiceSettingSideEffects(value: string): void {
    if (value === VoiceType.Voicer || value === VoiceType.EmotionTransfer) {
      this.formGroup.controls["voice"].clearValidators();
    } else {
      this.formGroup.controls["voice"].setValidators(Validators.required);
    }
    this.formGroup.controls["voice"].updateValueAndValidity();

    const translCtrl = this.formGroup.get("targetTranslationLanguage");
    if (value === VoiceType.EmotionTransfer) {
      translCtrl.setValidators([Validators.required]);
    } else {
      translCtrl.clearValidators();
    }
    translCtrl.updateValueAndValidity();
  }

  handleVoicerSettingChange() {
    this.formGroup.controls["voiceSettingType"].valueChanges.subscribe(
      (value: string) => this.applyVoiceSettingSideEffects(value)
    );

    this.formGroup.get("voiceProvider")?.valueChanges.subscribe((provider) => {
      if (
        this.formGroup.get("language")?.value &&
        this.formGroup.get("region")?.value
      ) {
        const languageRegion = `${this.formGroup.get("language").value}-${
          this.formGroup.get("region").value
        }`;
        this.getVoicesAndLexicons(languageRegion, false);
      }
    });
  }

  onSyntheticVoiceClick() {
    this.formGroup.controls["voiceSettingType"].setValue(VoiceType.Synthetic);
  }

  onVoiceChange(value: string) {
    const voiceDetail = this.voices.find((voice) => {
      if (voice.voiceType === "ElevenLabs") {
        this.voicerReadingSpeed = "createProject.speedRangeElevenLabs";
      }
      return voice.shortName === value;
    });
    this.voiceStyle = voiceDetail.styleList
      ? voiceDetail.styleList.split(",")
      : [];
  }

  public get filteredVoices(): any[] {
    const provider = this.formGroup.get("voiceProvider")?.value;
    if (!provider || provider === "All" || provider === "") {
      return this.voices || [];
    }
    return (this.voices || []).filter((v) => v.createdBy === provider);
  }

  private extractVoiceProviders(voices: any[]): void {
    if (!voices || voices.length === 0) {
      this.voiceProviders = [];
      return;
    }
    const providers = new Set(voices.map((v) => v.createdBy).filter(Boolean));
    this.voiceProviders = ["All", ...Array.from(providers)].sort();
  }

  public playVoicePreview(voice: any, event: Event): void {
    event.stopPropagation();

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    if (this.playingVoiceId === voice.shortName) {
      this.playingVoiceId = null;
      return;
    }

    // Get signed URL from backend
    this.textToSpeechService
      .getVoicePreviewSignedUrl(voice.shortName, voice.locale)
      .subscribe(
        (response) => {
          if (response.isSuccess && response.data) {
            this.currentAudio = new Audio(response.data);
            this.playingVoiceId = voice.shortName;

            this.currentAudio.addEventListener("ended", () => {
              this.playingVoiceId = null;
              this.currentAudio = null;
              this.cdk.detectChanges();
            });

            this.currentAudio.addEventListener("error", () => {
              console.error("Error playing audio");
              this.playingVoiceId = null;
              this.currentAudio = null;
              this.cdk.detectChanges();
            });

            this.currentAudio.play().catch((err) => {
              console.error("Error playing audio:", err);
              this.playingVoiceId = null;
              this.currentAudio = null;
            });
          } else {
            console.warn("No preview URL available for this voice");
          }
        },
        (error) => {
          console.error("Error fetching signed URL:", error);
        }
      );
  }

  public isVoicePlaying(voiceShortName: string): boolean {
    return this.playingVoiceId === voiceShortName;
  }

  public truncateVoiceName(voiceName: string, maxLength: number = 50): string {
    if (!voiceName) return "";
    if (voiceName.length <= maxLength) return voiceName;
    return voiceName.substring(0, maxLength) + "...";
  }

  formGroupChangeDetection() {
    this.formGroup.valueChanges.subscribe((x) => {
      if (this.isAllFilesUploaded) {
        this.disableControl = !this.formGroup.valid;
      }
    });
  }

  editCommonLogic() {
    this.editProject(); // edit project
    this.projectValueBackUp();
  }

  handleProjectEdit() {
    if (!this.project) {
      this.isEdit = false;
      this.pageTitle = "createProject.title";
      if (
        this.activatedRoute.snapshot.queryParamMap.get("restore") !== "true"
      ) {
        this.formGroup.patchValue({ voiceSettingType: VoiceType.Synthetic });
      }
      this.formPageService
        .shouldAutoRestore(this.activatedRoute.queryParams)
        .pipe(
          filter((isAutoRestore: boolean) => !isAutoRestore),
          tap(() => {
            this.getVoicesAndLexicons(this.latestLanguageRegionValue, false);
          })
        )
        .subscribe();
    } else {
      if (this.project.associatedAudioLufs) {
        this.formGroup
          .get("audioAssociatedLufs")
          .patchValue(this.project.associatedAudioLufs.toString());
      }

      this.projectId = this.project.id;
      this.isEdit = true;
      this.mainButtonText =
        this.project.status === ProjectStatus.WaitForTTML
          ? "createProject.continueButton"
          : "createProject.restartButton";
      if (this.project.videoAssetId || this.project.audioAssetId) {
        this.selectedAsset =
          this.assetSelectionService.getSelection()?.assets || [];
        const videoAsset$ = this.project.videoAssetId
          ? this.assetService.getAsset(this.project.videoAssetId)
          : of(null);

        const audioAsset$ = this.project.audioAssetId
          ? this.assetService.getAsset(this.project.audioAssetId)
          : of(null);

        forkJoin({
          video: videoAsset$,
          audio: audioAsset$,
        }).subscribe(({ video, audio }) => {
          if (video) {
            const videoAssetIndex = this.selectedAsset?.findIndex(
              (asset) => asset.assetType === "video"
            );
            if (videoAssetIndex >= 0) {
              this.selectedAsset[videoAssetIndex] = video;
            } else {
              this.selectedAsset.push(video);
            }
          }

          if (audio) {
            const audioAssetIndex = this.selectedAsset?.findIndex(
              (asset) => asset.assetType === "audio"
            );
            if (audioAssetIndex >= 0) {
              this.selectedAsset[audioAssetIndex] = audio;
            } else {
              this.selectedAsset.push(audio);
            }
          }
          this.assetSelectionService.setSelection({
            assets: this.selectedAsset,
          });
          this.editCommonLogic();
        });
      } else {
        this.editCommonLogic();
      }
    }
  }

  projectValueBackUp() {
    this.projectDataBackup = {
      voice: this.project.voice.split("/")[0],
      lexicon: this.project.lexiconUri,
      speed: this.project.speedRate,
      automatedSpeedRate: this.project.automatedSpeedRate,
      frameRate: this.project.frameRate,
      isAutomatedSpeed: this.project.isAutomatedSpeedRate,
    };
  }

  // projectParameters
  setupFormControls() {
    this.formGroup = this.formBuilder.group({
      projectName: ["", noWhitespaceValidator],
      templateName: [""],
      language: [
        this.projectId ? "" : environment.project.languages.default,
        Validators.required,
      ],
      region: [
        this.projectId ? "" : this.regions[0].value,
        Validators.required,
      ],
      voice: ["", Validators.required],
      voiceStyle: [""],
      lexicon: [" "],
      lufs: [this.defaultLuf],
      audioAssociatedLufs: "",
      startTimeCode: ["00:00:00:00", Validators.required],
      speed: [environment.project.speedRate.default, Validators.required],
      frameRate: [0, [Validators.required, checkFrameRate]],
      automatedSpeedRate: [
        environment.project.maximumSpeedRate.default,
        Validators.required,
      ],
      isAutomatedSpeed: [true, Validators.required],
      isAutomatedMix: [false],
      usersToNotify: [],
      audioInputDevice: [],
      voiceSettingType: [""],
      targetTranslationLanguage: [""],
      styleTransferEnabled: [false],
      voicerReadingSpeed: [190, Validators.required],
      automatedSoundCorrection: [true],
      setTimeReferenceFromProject: [false],
      selectedTabIndex: [0],
      uniqueProjectName: [""],
      voiceProvider: [""],
    });

    this.formGroup.get("lexicon").valueChanges.subscribe((value) => {
      if (!value) {
        // If the value is null (i.e., "None" is selected), reset to pristine
        this.formGroup.get("lexicon").markAsPristine();
      }
    });
  }

  /* when we change the language , region need to be changed,
  so, region will be pulled out based on the language .*/
  onLanguageSelectionChange(
    objectValue: string,
    isPreset: boolean,
    lexiconId?: number
  ) {
    const selectedLanguage = environment.project.regions.list.find(
      (element) => {
        return element.key.toLowerCase() === objectValue.toLowerCase();
      }
    );

    if (selectedLanguage) {
      this.regions = selectedLanguage.value;
      if (!isPreset) {
        this.formGroup.patchValue({
          region: this.project ? this.project.region : this.regions[0].value, // the first item of the region list will be set by default
        });
      } else {
        this.formGroup.patchValue({
          region: this.formGroup.get("region").value, // the first item of the region list will be set by default
        });
      }
      this.onRegionSelectionChange(
        this.formGroup.get("region").value,
        isPreset,
        lexiconId
      );
    }
  }

  /* Based on language-region fetch the voices.*/
  onRegionSelectionChange(
    region: string,
    isPreset: boolean,
    lexiconId?: number
  ) {
    this.spinner = true;
    const languageRegion = this.formGroup.get("language").value + "-" + region;
    if (!isPreset) {
      this.formGroup.patchValue({ voice: "", lexicon: " " });
    }
    this.getVoicesAndLexicons(languageRegion, isPreset, lexiconId);
  }

  get isVoicerSelected(): boolean {
    return this.formGroup.get("voiceSettingType").value === VoiceType.Voicer;
  }

  get isEmotionTransferSelected(): boolean {
    return (
      this.formGroup.get("voiceSettingType").value === VoiceType.EmotionTransfer
    );
  }

  onUserToNotifyChange(userId: number) {
    this.usersToNotify
      .filter((e) => e.id === userId)
      .map((x) => (x.isSelected = true));
    this.formGroup.patchValue({
      selectedUsersToNotifyList: this.usersToNotify
        .filter((user) => user.isSelected)
        .map((user) => user.id),
    });
  }

  removeUserFromUsersToNotify(userId: number) {
    this.usersToNotify
      .filter((e) => e.id === userId)
      .map((x) => (x.isSelected = false));
    this.formGroup.patchValue({
      selectedUsersToNotifyList: this.usersToNotify
        .filter((user) => user.isSelected)
        .map((user) => user.id),
    });
  }

  public onAutomatedSpeedChange() {
    this.variableSpeedWarning = false;
    if (
      this.formGroup.get("isAutomatedSpeed").value &&
      this.formGroup.get("automatedSpeedRate").value <=
        this.formGroup.get("speed").value
    ) {
      this.variableSpeedWarning = true;
    }
  }

  validateStartTimeCode() {
    const startTimeCodeValue = this.formGroup.get("startTimeCode").value;
    if (startTimeCodeValue.includes("_")) {
      this.formGroup.patchValue({
        startTimeCode: startTimeCodeValue.replaceAll("_", "0"),
      });
    }
  }

  getEnvironmentName(): string {
    const apiUrl = environment.phoenixApiUrl;
    const prefix = "https://api-";
    const suffix = ".difuzevox.com/api/";

    // Extract the substring between prefix and suffix
    if (apiUrl.includes(prefix) && apiUrl.includes(suffix)) {
      return apiUrl.substring(
        apiUrl.indexOf(prefix) + prefix.length,
        apiUrl.indexOf(suffix)
      );
    }

    return "unknown";
  }

  async buildReview() {
    this.onStartTimeCodeChange();
    const voiceName = this.formGroup.get("voice").value;
    const style = this.formGroup.get("voiceStyle");

    if (!this.isEmotionTransferSelected) {
      this.voices = await this.textToSpeechService
        .getVoicesByLanguageAndRegion(this.latestLanguageRegionValue)
        .toPromise();
    }

    let voiceDetail;

    if (this.voices) {
      voiceDetail = this.voices.find((voice) => {
        return voice.shortName === voiceName;
      });
    }

    const videoAssetIndex = this.selectedAsset?.findIndex(
      (asset) => asset.assetType === "video"
    );
    const audioAssetIndex = this.selectedAsset?.findIndex(
      (asset) => asset.assetType === "audio"
    );
    const audioPath =
      audioAssetIndex >= 0
        ? this.selectedAsset[audioAssetIndex]?.assetProxy.find(
            (proxy) => proxy.mime === "audio/wav"
          )?.key
        : "";
    this.projectReview = {
      projectNames: [],
      uniqueProjectNames: [],
      language: this.formGroup.get("language").value,
      region: this.formGroup.get("region").value,
      voice: voiceName
        ? voiceName +
          "/" +
          voiceDetail?.maskedShortName +
          "/" +
          voiceDetail?.createdBy +
          "/" +
          style
        : this.project?.voice
        ? this.project.voice
        : "",
      voiceSetting: this.formGroup.get("voiceSettingType").value,
      voicerReadingSpeed: this.formGroup.get("voicerReadingSpeed").value,
      lexiconUri: this.formGroup.get("lexicon").value,
      startTimeCode: this.formGroup.get("startTimeCode").value,
      speedRate: this.formGroup.get("speed").value,
      usersToNotify: this.usersToNotify
        .filter((e) => e.isSelected === true)
        .map(({ id }) => id),
      isRestarting: this.project ? true : false,
      isAutomatedSpeed: this.isVoicerSelected
        ? false
        : this.formGroup.get("isAutomatedSpeed").value,
      automatedSpeedRate: this.formGroup.get("automatedSpeedRate").value,
      usersToNotifyEmails: this.usersToNotify.filter(
        (e) => e.isSelected === true
      ),
      isAutomatedMix: this.isVoicerSelected
        ? false
        : this.formGroup.get("isAutomatedMix").value,
      frameRate: this.formGroup.get("frameRate").value,
      maskedVoice: voiceName ? voiceDetail?.maskedShortName : "",
      languageName: this.languages.find((language) => {
        return language.value === this.formGroup.get("language").value;
      }).key,
      lexiconName:
        this.formGroup.get("lexicon").value?.trim() === "" ||
        this.isVoicerSelected
          ? ""
          : this.lexicons.find((lexicon) => {
              return lexicon.fileUri === this.formGroup.get("lexicon").value;
            })?.name,
      fileDetails: [],
      templateName: this.selectedPresetTemplate
        ? this.selectedPresetTemplate.name
        : "",
      stereoFilePath: audioPath,
      clientId: this.selectedPresetTemplate
        ? this.selectedPresetTemplate.clientId
        : null,
      templateId: this.selectedPresetTemplate
        ? this.selectedPresetTemplate.id
        : null,
      lexiconId:
        this.formGroup.get("lexicon").value?.trim() === "" ||
        this.isVoicerSelected
          ? null
          : this.lexicons.find((lexicon) => {
              return lexicon.fileUri === this.formGroup.get("lexicon").value;
            })?.id,
      humanVoiceRecorderId:
        this.formGroup.get("audioInputDevice").value || null,
      videoAssetId:
        videoAssetIndex >= 0
          ? this.selectedAsset[videoAssetIndex].id
          : this.project?.videoAssetId || null,
      audioAssetId:
        audioAssetIndex >= 0
          ? this.selectedAsset[audioAssetIndex].id
          : this.project?.audioAssetId || null,
      automatedSoundCorrection: this.formGroup.get("automatedSoundCorrection")
        .value,
      setTimeReferenceFromProject: this.formGroup.get(
        "setTimeReferenceFromProject"
      ).value,
      isGeneratedTtmlFile: this.project
        ? this.project.isGeneratedTtmlFile
        : this.isXmlFileUploaded,
      lufs: Number(this.formGroup.get("lufs").value),
      lufValue: this.lufs.find(
        (luf) => luf.value === this.formGroup.get("lufs").value
      ),
      associatedAudioLufs: this.formGroup.get("audioAssociatedLufs").value,
      targetTranslationLanguage: this.isEmotionTransferSelected
        ? this.formGroup.get("targetTranslationLanguage").value
        : undefined,
      styleTransferEnabled: this.isEmotionTransferSelected
        ? !!this.formGroup.get("styleTransferEnabled").value
        : undefined,
    };

    // build the project/files-attached section - create project scenario only
    if (!this.projectId) {
      this.getProjectFilesAttached();
    }
  }

  getProjectFilesAttached() {
    this.projectFilesAttached = [];
    if (this.fileUploadContexts.length === 1) {
      this.projectFilesAttached.push({
        projectName: this.formGroup.get("projectName").value,
        fileName: [this.fileUploadContexts[0].file.name],
        assetId: this.fileUploadContexts[0].assetId,
      });
    } else if (
      this.fileUploadContexts.length === 2 &&
      this.fileUploadContexts[0].file.name.split(".").reverse()[0] !==
        this.fileUploadContexts[1].file.name.split(".").reverse()[0]
    ) {
      const fileNames = this.fileUploadContexts.map((files) => {
        return files.file.name;
      });
      this.projectFilesAttached.push({
        projectName: this.formGroup.get("projectName").value,
        fileName: [...fileNames],
        assetId: this.fileUploadContexts[0].assetId,
      });
    } else {
      this.fileUploadContexts.forEach((file) => {
        this.projectFilesAttached.push({
          projectName: file.projectName,
          fileName: [file.file.name],
          assetId: file.assetId,
        });
      });
    }
  }

  public onFileSelectedFromScriptWriting($event: any) {
    if ($event.uniqueProjectName) {
      const removedFile = this.fileUploadContexts.find(
        (file) => file.assetId === $event.uniqueProjectName
      );
      this.cancelSingleFileUpload(removedFile);
    }

    this.onFileSelected($event.fileData);
  }

  // fileUpload
  public async onFileSelected($event: any): Promise<void> {
    this.uploadCompleted = false;
    this.buildFileUploadContexts($event.target.files as unknown as FileList);
    await this.calculateSilenceOnCreate();
    // this.addControls(); // adds projectName controls on the go.
    this.cdk.detectChanges();
    this.addProjectNameValidation(); // add projectName validation
    if (this.isProjectCreationValid() || this.validateProjectFiles()) {
      // trying to create project with wav and ttml and more than one
      alert(this.translateService.instant("createProject.upload.mixUpload"));
      return of().toPromise();
    }
    await this.prepareFiles();
    if (
      this.scriptWritingComponent &&
      this.scriptWritingComponent.uploadFileComponent?.fileUploadInput
    ) {
      this.scriptWritingComponent.uploadFileComponent.fileUploadInput.nativeElement.value =
        "";
    } else if (this.uploadFileComponent?.fileUploadInput) {
      this.uploadFileComponent.fileUploadInput.nativeElement.value = "";
    }
    this.startUploadingFiles();
  }

  isProjectCreationValid(): boolean {
    const isMixProject = this.fileUploadContexts.filter(
      (files) => !files.isValidated
    );
    return isMixProject.length > 0;
  }

  hasFileAttached() {
    return this.fileUploadContexts.filter((x) => !x.isDeleted).length > 0;
  }

  addProjectNameValidation() {
    const filesSupportingUploadScenarios = this.fileUploadContexts.filter(
      (file) => file.isValidated && !file.isDeleted
    );
    if (
      (filesSupportingUploadScenarios &&
        filesSupportingUploadScenarios.length) === 1 ||
      (filesSupportingUploadScenarios.length === 2 &&
        filesSupportingUploadScenarios[0].file.type !==
          filesSupportingUploadScenarios[1].file.type)
    ) {
      this.formGroup.controls["projectName"].setValidators([
        Validators.required,
        noWhitespaceValidator,
      ]);
      if (!this.projectId) {
        // project creation scenario
        const projectName =
          this.formGroup.get("projectName").value.trim() === "";
        if (projectName) {
          this.patchProjectName();
        }
      }
    } else {
      if (this.formGroup.get("projectName").value !== "" && !this.projectId) {
        // create project-scenario
        if (
          filesSupportingUploadScenarios.length > 0 ||
          this.userEnteredProjectName.trim() === ""
        ) {
          this.formGroup.patchValue({ projectName: "" });
          this.userEnteredProjectName = "";
        }
      }
      this.formGroup.controls["projectName"].clearValidators();
    }
    this.formGroup.controls["projectName"].updateValueAndValidity();
  }

  // during project creation
  patchProjectName() {
    // project creation,if ttml it already uploaded, uploading wav should override the projectName
    if (this.userEnteredProjectName.trim() === "") {
      const isWAVExist = this.fileUploadContexts.filter(
        (x) => x.file.type.split("/")[1] === "wav" && !x.isDeleted
      )[0];
      isWAVExist
        ? this.formGroup.patchValue({
            projectName: isWAVExist.file.name.substr(
              0,
              isWAVExist.file.name.lastIndexOf(".")
            ),
          })
        : this.formGroup.patchValue({
            projectName: this.fileUploadContexts[0].isDeleted
              ? this.fileUploadContexts[1].file.name.substr(
                  0,
                  this.fileUploadContexts[1].file.name.lastIndexOf(".")
                )
              : this.fileUploadContexts[0].file.name.substr(
                  0,
                  this.fileUploadContexts[0].file.name.lastIndexOf(".")
                ),
          });
    }
  }

  // adds text box controls which all us to rename the projects, in case of bulk project creation i.e., more than one at a time
  addControls() {
    this.fileUploadContexts.forEach((file) => {
      this.formGroup.addControl(
        `${file.file.name}-${file.assetId}`,
        this.formBuilder.control("", [Validators.required])
      );
    });
  }

  isProjectNameToBeDisplayed(): boolean {
    const filesSupportingUploadScenarios = this.fileUploadContexts.filter(
      (file) => file.isValidated
    );
    if (
      (filesSupportingUploadScenarios &&
        filesSupportingUploadScenarios.length) === 1 ||
      (filesSupportingUploadScenarios.length === 2 &&
        filesSupportingUploadScenarios[0].file.type !==
          filesSupportingUploadScenarios[1].file.type)
    ) {
      return false;
    } else if (this.projectId) {
      return false;
    } else {
      return true;
    }
  }

  /** temporary situation to offer display of files although checksum happens under the hood
   *  for each file one by one. This method will be removed when checksums will be computed
   *  progressively along with parallel uploads of chunks
   */
  private buildFileUploadContexts(files: FileList) {
    for (let index = 0; index < files.length; index++) {
      const extension = "." + files[index].name.split(".").pop().toLowerCase();
      if (this.isRestrictedDocumentExtension(extension)) {
        this.filesAllowedWarning = true;
        return;
      }
      // In emotion transfer mode, only one script file is allowed at a time
      if (
        this.isEmotionTransferSelected &&
        this.isScriptSourceType(extension.substring(1))
      ) {
        const alreadyHasScript = this.unRemovedFileUploadContexts.some((f) =>
          this.isScriptSourceType(
            f.file.name.split(".").pop()?.toLowerCase() || ""
          )
        );
        if (alreadyHasScript) {
          this.filesAllowedWarning = true;
          return;
        }
      }
      if (this.authorizedFileExtensions.includes(extension)) {
        this.filesAllowedWarning = false;
        const fileContext: FileUploadContext = {
          file: files[index],
          assetId: "",
          upload: {
            remainingTime: Infinity,
            status: "",
            message: "",
            progress: 0,
          },
          projectName: "",
        };
        !this.projectId
          ? this.createProjectValidation(fileContext)
          : this.editProjectValidation(fileContext);
      } else {
        this.filesAllowedWarning = true;
        return;
      }
    }
  }

  createProjectValidation(fileContext: FileUploadContext) {
    // restrict user-file selection/upload
    const filterDifferentTypes = this.fileUploadContexts.filter(
      (x) => x.file.type !== fileContext.file.type && !x.isDeleted
    );
    const filterSameTypes = this.fileUploadContexts.filter(
      (x) => x.file.type === fileContext.file.type && !x.isDeleted
    );
    if (
      (filterDifferentTypes && filterDifferentTypes.length < 1) ||
      (filterSameTypes.length < 1 && filterDifferentTypes.length <= 1)
    ) {
      fileContext.isValidated = true;
      fileContext.upload.message = this.translateService.instant(
        "createProject.upload.inProgress"
      );
      fileContext.upload.status = Status.Uploading;
    } else {
      fileContext.isValidated = false;
    }
    fileContext.assetId = newUuid();
    fileContext.isEdited = true;
    fileContext.projectName = fileContext.file.name.substr(
      0,
      fileContext.file.name.lastIndexOf(".")
    );
    if (
      this.fileUploadContexts.length === 1 &&
      this.fileUploadContexts[0].file.type !== fileContext.file.type
    ) {
      fileContext.assetId = this.fileUploadContexts[0].assetId;
    }
    this.handleFileName(fileContext);
    this.fileUploadContexts.push(fileContext);
  }

  handleFileName(fileContext: FileUploadContext) {
    const fileName = fileContext.file.name.substr(
      0,
      fileContext.file.name.lastIndexOf(".")
    );
    const filter = this.fileUploadContexts.filter((context) =>
      context.file.name.includes(fileName)
    );
    if (filter.length > 0) {
      fileContext.projectName = `${fileName}_${filter.length}`;
      fileContext.file = new File(
        [fileContext.file],
        `${fileName}_${filter.length}.${fileContext.file.name.substr(
          fileContext.file.name.lastIndexOf(".") + 1,
          fileContext.file.name.length
        )}`,
        {
          type: fileContext.file.type,
        }
      );
    }
  }

  // edit scenario
  editProjectValidation(fileContext: FileUploadContext) {
    const filterDifferentTypes = this.fileUploadContexts.filter(
      (x) =>
        x.file.name.split(".").reverse()[0] !==
          fileContext.file.type.split("/")[1] && !x.isDeleted
    );
    const filterSameTypes = this.fileUploadContexts.filter(
      (x) =>
        x.file.name.split(".").reverse()[0] ===
          fileContext.file.type.split("/")[1] && !x.isDeleted
    );
    if (filterDifferentTypes.length === 0 || filterSameTypes.length === 0) {
      // double validate files of similar types, as this is the update scenario, we should not allow to upload files of similar types
      if (filterSameTypes.length === 0) {
        fileContext.isValidated = true;
      }
    }
    fileContext.assetId = this.project.uniqueProjectName;
    fileContext.isEdited = true;
    fileContext.projectName = fileContext.file.name.substr(
      0,
      fileContext.file.name.lastIndexOf(".")
    );
    this.handleFileName(fileContext);
    this.fileUploadContexts.push(fileContext);
  }

  public setAuthorizedFileExtensions(): void {
    const documentExtensions = this.isEmotionTransferSelected
      ? this.documentUploadExtensions
      : [];
    const allowedExtensions = Array.from(
      new Set([
        ...environment.project.authorizedFileExtensions,
        ...documentExtensions,
      ])
    );
    const extensions = this.allowXmlOnly
      ? allowedExtensions.filter((ex) => ex === ".xml")
      : allowedExtensions.filter((ex) => ex !== ".xml");
    this.authorizedExtensions = extensions.join(", ");
  }

  public get authorizedFileExtensions(): string {
    const documentExtensions = this.isEmotionTransferSelected
      ? this.documentUploadExtensions
      : [];
    return Array.from(
      new Set([
        ...environment.project.authorizedFileExtensions,
        ...documentExtensions,
      ])
    ).join(", ");
  }

  private isRestrictedDocumentExtension(extension: string): boolean {
    return (
      this.documentUploadExtensions.includes(extension.toLowerCase()) &&
      !this.isEmotionTransferSelected
    );
  }

  public dragOverHandler($event: DragEvent): boolean {
    $event.preventDefault();
    return false;
  }

  public dragLeaveHandler($event: DragEvent): boolean {
    $event.preventDefault();
    return false;
  }

  public setShowProjectSection() {
    this.showProjectSection = !this.showProjectSection;
    this.pageId = undefined;
  }

  public onDropHandlerFromScriptWriting($event: any) {
    if ($event.uniqueProjectName) {
      const removedFile = this.fileUploadContexts.find(
        (file) => file.assetId === $event.uniqueProjectName
      );
      this.cancelSingleFileUpload(removedFile);
    }
    this.dropHandler($event.fileData);
  }

  public async dropHandler($event: DragEvent): Promise<boolean> {
    $event.preventDefault();
    if (this.canSelectFiles) {
      this.uploadCompleted = false;
      this.buildFileUploadContexts($event.dataTransfer.files);
      await this.calculateSilenceOnCreate();
      // this.addControls(); // add projectName controls on the go.
      this.cdk.detectChanges();
      this.addProjectNameValidation(); // add projectName validation
      if (this.isProjectCreationValid() || this.validateProjectFiles()) {
        // trying to create project with wav and ttml and more than one
        alert(this.translateService.instant("createProject.upload.mixUpload"));
        return of(false).toPromise();
      }
      await this.prepareFiles();
      this.startUploadingFiles();
    }
    return of(false).toPromise();
  }

  public get canSelectFiles() {
    return (
      this.fileUploadContexts.length === 0 ||
      (this.fileUploadContexts.length > 0 &&
        this.fileUploadContexts.length ===
          this.fileUploadContexts.filter(
            (fuc) => fuc.upload.status === Status.UploadSuccess
          ).length)
    );
  }

  public get canDisplayFilesListing() {
    return this.fileUploadContexts.length > 0;
  }

  public get isXmlFileUploaded() {
    return (
      this.canDisplayFilesListing &&
      this.fileUploadContexts.some((fileUploadContext) =>
        fileUploadContext.file.name.includes(".xml")
      )
    );
  }

  /* this separation is temporary as long as checksum is computed from the beginning */
  public async prepareFiles(): Promise<void> {
    this.markExceedingFilesSizeByWarning();
    await this.preProcessUnExceedingFilesSize();
  }

  private async preProcessUnExceedingFilesSize() {
    for (let index = 0; index < this.fileUploadContexts.length; index++) {
      const fileUploadContext = this.fileUploadContexts[index];
      if (
        fileUploadContext.file.size <= environment.project.maximumFileSize &&
        fileUploadContext.isValidated &&
        fileUploadContext.upload.status !== Status.UploadSuccess
      ) {
        await this.preProcessFileUploadContext(fileUploadContext);
      }
    }
  }

  public markExceedingFilesSizeByWarning() {
    for (let index = 0; index < this.fileUploadContexts.length; index++) {
      const fileUploadContext = this.fileUploadContexts[index];
      if (fileUploadContext.file.size > environment.project.maximumFileSize) {
        fileUploadContext.upload.status = Status.FileSizeExceedingFailure;
        fileUploadContext.upload.message = this.translateService.instant(
          "createProject.upload.maxFileSizeReached"
        );
        fileUploadContext.upload.message =
          fileUploadContext.upload.message.replace(
            "{maxFileSizeReached}",
            environment.project.maximumFileSize as unknown as string
          );
      }
    }
  }

  public cancelSingleFileUpload(fileUploadContext: FileUploadContext) {
    fileUploadContext.isDeleted = true;
    const fileName = fileUploadContext.file.name.split(".");
    const fileExtension = fileName[fileName.length - 1];
    if (
      fileExtension.toLowerCase() === "xml" &&
      this.project &&
      this.projectReview
    ) {
      this.project.isGeneratedTtmlFile = true;
      this.projectReview.isGeneratedTtmlFile = true;
    }
    this.addProjectNameValidation();
    this.deleteFiles(fileUploadContext);
    this.recentlyDeletedFile = fileUploadContext;
  }

  undo(fileUploadContext: FileUploadContext) {
    fileUploadContext.isDeleted = false;
    this.addProjectNameValidation();
  }

  // schedule deletion of files if no action is performed after cancellation
  deleteFiles(fileUploadContext: FileUploadContext) {
    this.spinner = true;
    setTimeout(async () => {
      if (fileUploadContext.isDeleted) {
        this.formGroup.removeControl(
          `${fileUploadContext.file.name}-${fileUploadContext.assetId}`
        );
        if (!this.project) {
          this.cancel(fileUploadContext);
          this.spinner = false;
        } else {
          const index = this.fileUploadContexts.findIndex(
            (fuc) => fuc.file.name === fileUploadContext.file.name
          );
          if (
            this.project?.stereoFilePath?.includes(fileUploadContext.file.name)
          ) {
            this.project.stereoFilePath = "";
          }
          this.fileUploadContexts.splice(index, 1);
        }
        if (fileUploadContext.upload.status === Status.UploadSuccess) {
          await this.deleteFileFromS3(
            `${fileUploadContext.assetId}/${fileUploadContext.file.name}`
          );
        }
      }
    }, environment.snackBarDurationLong + 500);
  }

  // remove file from fileUpload list and abort upload if in progress..
  private cancel(fileUploadContext: FileUploadContext): void {
    const index = this.fileUploadContexts.findIndex(
      (fuc) => fuc.file.name === fileUploadContext.file.name
    );
    if (index >= 0) {
      this.abortUploadingFile(fileUploadContext);
      this.fileUploadContexts.splice(index, 1);
    }
    if (this.areFilesAllUploaded) {
      this.processUploadCompleted();
    }
  }

  public async abortUploadingFile(
    fileUploadContext: FileUploadContext
  ): Promise<void> {
    if (fileUploadContext.multiPartUploadHandler) {
      await fileUploadContext.multiPartUploadHandler.notifier.unsubscribe();
      await fileUploadContext.multiPartUploadHandler.abortMultipartUpload();
      delete fileUploadContext.multiPartUploadHandler;
    }
    fileUploadContext.upload.message = " ";
    fileUploadContext.upload.status = Status.Uploading;
    fileUploadContext.upload.progress = 0;
  }

  // delete files from s3
  public async deleteFileFromS3(cancelledFileAssetId: string): Promise<void> {
    await this.deleteFile(cancelledFileAssetId);
    this.updateScriptWritingComponent();
    this.updateProjectReplies();
    await this.buildReview();
    this.spinner = true;
    this.buildProject();
    this.saveProjectReview();
  }

  private async deleteFile(cancelledFileAssetId: string): Promise<void> {
    await this.projectService.discardFile([cancelledFileAssetId]).toPromise();
  }

  private updateScriptWritingComponent(): void {
    if (this.scriptWritingComponent) {
      this.scriptWritingComponent.spinner = true;
      this.scriptWritingComponent.project.replies = [];
      this.scriptWritingComponent.checkForReplies();
    }
  }

  private updateProjectReplies(): void {
    if (!this.isXmlFileUploaded && !this.scriptWritingComponent) {
      if (!this.project) {
        this.project = {} as Project;
      }
      this.project.replies = [];
    }
  }

  private saveProjectReview(): void {
    this.projectService.save(this.projectReview).subscribe((data) => {
      this.formGroup.patchValue({
        uniqueProjectName: data.data.uniqueProjectName,
      });
      this.project = data.data;
      this.projectId = data.data.id;
      this.spinner = false;
    });
  }

  public confirmCancelUpload() {
    for (let index = 0; index < this.fileUploadContexts.length; index++) {
      const fileUploadContext = this.fileUploadContexts[index];
      this.cancel(fileUploadContext);
      if (fileUploadContext.upload.status === Status.UploadSuccess) {
        this.cancelledAssetIds.push(
          `${fileUploadContext.assetId}/${fileUploadContext.file.name}`
        );
      }
      index--; // since this.fileUploadContexts.length has decremented
    }
  }

  public get unRemovedFileUploadContexts() {
    return this.fileUploadContexts.filter((files) => !files.isDeleted);
  }

  /**
   * Pre-process file upload
   * @param fileUploadContext
   * @private
   */
  private async preProcessFileUploadContext(
    fileUploadContext: FileUploadContext
  ) {
    const fileExtension = fileUploadContext.file.name.split(".").reverse()[0];
    fileUploadContext.upload.status = Status.Uploading;

    const uploadAuthorizationBody: UploadAuthorizationBody = {
      assetId:
        fileUploadContext.assetId +
        "/" +
        "source-file" +
        "/" +
        fileUploadContext.file.name.substr(
          0,
          fileUploadContext.file.name.lastIndexOf(".")
        ),
      fileExtension,
      fileSize: fileUploadContext.file.size,
      numberOfFiles: this.fileUploadContexts.length,
      uploadType: "fileUpload",
      projectData: JSON.stringify({
        name: encodeURIComponent(this.formGroup.get("projectName").value),
        language: this.formGroup.get("language").value,
        frameRate: this.formGroup.get("frameRate").value,
        startTimeCode: this.formGroup.get("startTimeCode").value,
        usersToNotify: this.usersToNotify
          ?.filter((e) => e.isSelected === true)
          .map(({ id }) => id),
      }),
      noStepFunction:
        fileExtension === "xml" && !this.isEmotionTransferSelected
          ? false
          : true,
    };

    fileUploadContext.authorization = await this.fileService
      .createUploadAuthorization(uploadAuthorizationBody)
      .toPromise();

    if (
      fileExtension.toLowerCase() === "xml" &&
      this.project &&
      this.projectReview
    ) {
      this.project.isGeneratedTtmlFile = false;
      if (this.projectReview) {
        this.projectReview.isGeneratedTtmlFile = false;
      }
      await this.deleteFileFromS3(
        `${fileUploadContext.assetId}/${this.project.name}.xml`
      );
    }
  }

  public async startUploadingFiles(): Promise<void> {
    for (let index = 0; index < this.fileUploadContexts.length; index++) {
      const fileUploadContext = this.fileUploadContexts[index];
      if (
        fileUploadContext.upload.progress === 0 &&
        fileUploadContext.isValidated &&
        fileUploadContext.upload.status === Status.Uploading
      ) {
        fileUploadContext.upload.message = "";
        this.startMultiPartUpload(fileUploadContext);
      }
    }
  }

  private startMultiPartUpload(fileUploadContext: FileUploadContext): void {
    fileUploadContext.multiPartUploadHandler = new MultiPartUploadHandler(
      this.fileService,
      this.http,
      {
        notificationHandler: (notification) =>
          this.handleNotification(notification),
        file: fileUploadContext.file,
        uploadAuthorization: fileUploadContext.authorization,
        includeLufs: fileUploadContext.file.type.includes("wav"),
      }
    );
  }

  private get areFilesAllUploaded(): boolean {
    let allFilesUploaded = false;
    const activeFiles = this.fileUploadContexts.filter((fuc) => !fuc.isDeleted);

    const notUploaded = activeFiles.filter(
      (fuc) => fuc.upload.status !== Status.UploadSuccess
    );
    allFilesUploaded = notUploaded.length === 0;

    return allFilesUploaded;
  }

  private processUploadCompleted() {
    this.uploadCompleted = true;
    this.projectService.onChangeFileUploadContexts(this.fileUploadContexts);
    if (this.project && this.isXmlFileUploaded) {
      this.project.isGeneratedTtmlFile = false;
      if (this.projectReview) {
        this.projectReview.isGeneratedTtmlFile = false;
      }
      this.isEdit = false;
      this.onStartTimeCodeChange();
    }
  }

  public get isAllFilesUploaded(): boolean {
    const activeFiles = this.fileUploadContexts.filter(
      (file) => !file.isDeleted
    );
    const filesUploadedSuccessfully =
      activeFiles.length &&
      activeFiles.filter((file) => file.upload.status === Status.UploadSuccess);
    return filesUploadedSuccessfully.length === activeFiles.length;
  }

  checkForReplies() {
    if (this.scriptWritingComponent) {
      this.scriptWritingComponent.spinner = true;
      this.scriptWritingComponent.retriedCount = 0;
      this.scriptWritingComponent.project.replies = [];

      this.scriptWritingComponent.checkForReplies();
    }
  }

  private handleNotification(notification: Notification): void {
    const foundFileUploadContext = this.fileUploadContexts.find(
      (fileUploadContext) =>
        fileUploadContext.file &&
        fileUploadContext.file.name === notification.fileName
    );
    if (!foundFileUploadContext) {
      // might have been removed from the list meanwhile
      return;
    }
    if (notification.status === "progression") {
      foundFileUploadContext.upload.progress = notification.value;
      foundFileUploadContext.upload.status = Status.Uploading;
      foundFileUploadContext.upload.message = "";
    } else if (notification.status === "remainingTime") {
      foundFileUploadContext.upload.remainingTime = notification.value;
      foundFileUploadContext.upload.status = Status.Uploading;
    } else if (
      notification.status === "completionTime" &&
      (foundFileUploadContext.file.type != "text/xml" ||
        foundFileUploadContext.retakesGenerationStoredInDb === true)
    ) {
      foundFileUploadContext.upload.message = this.translateService.instant(
        "createProject.upload.completed"
      );
      foundFileUploadContext.upload.status = Status.UploadSuccess;
      foundFileUploadContext.lufs = notification.lufs;
      if (notification.lufs) {
        this.formGroup
          .get("audioAssociatedLufs")
          .patchValue(notification.lufs.toString());
      }
      this.checkForReplies();
    } else if (
      notification.status === "completionTime" &&
      !(
        foundFileUploadContext.file.type != "text/xml" ||
        foundFileUploadContext.retakesGenerationStoredInDb === true
      )
    ) {
      foundFileUploadContext.upload.status = Status.Uploading;
    } else {
      /* if (notification.status === 'networkIssue') */
      const networkIssue = this.translateService.instant(
        "createProject.upload.networkIssueRetry"
      );
      foundFileUploadContext.upload.message = networkIssue.replace(
        "{networkIssueRetryInterval}",
        notification.value.toString()
      );
      foundFileUploadContext.upload.status = Status.UploadFailure;
    }

    if (this.areFilesAllUploaded) {
      this.processUploadCompleted();
    }
    // Trigger change detection to update button states
    // Use setTimeout to ensure the next change detection cycle picks up the changes
    setTimeout(() => {
      this.cdk.detectChanges();
    }, 0);
  }

  public getFormattedDurationMessage(fileContext: FileUploadContext): string {
    return this.durationFormatterService.getUserReadableMessage(
      fileContext.upload.remainingTime,
      "createProject.upload",
      "remainingTime"
    );
  }

  buildProject() {
    this.disableControl = true; // To avoid multiple clicks of the save button.
    this.projectReview.projectNames = [];
    this.projectReview.uniqueProjectNames = [];
    this.projectReview.isGeneratedTtmlFile =
      !!this.project?.isGeneratedTtmlFile;
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    if (
      (this.fileUploadContexts.length === 1 &&
        this.getFileType(0) === "wav" &&
        !audioAsset) ||
      (this.fileUploadContexts.length === 1 &&
        this.isScriptSourceType(this.getFileType(0))) ||
      (this.fileUploadContexts.length === 2 &&
        !audioAsset &&
        this.fileUploadContexts[0].file.name.split(".").reverse()[0] !==
          this.fileUploadContexts[1].file.name.split(".").reverse()[0])
    ) {
      this.setProjectNameAndFilePathForSingleProject(); // creating a single project, either by xml/wav or both together
    } else if (
      (this.fileUploadContexts.length && this.isAllFilesAreWav) ||
      (this.isAllFilesAreXml && !audioAsset)
    ) {
      this.setProjectNamesAndFilePaths(); // bulk project creation
    } else {
      this.projectReview.projectNames.push(
        this.formGroup.get("projectName").value
      );
      this.projectReview.uniqueProjectNames = [
        this.project?.uniqueProjectName || this.uniqueProjectName
          ? this.uniqueProjectName
          : newUuid(),
      ];
    }
    const hasWav = this.fileUploadContexts.filter(
      (f) => f.file.name.split(".").reverse()[0] === "wav"
    );

    const videoAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "video"
    );
    const audioPath = audioAsset
      ? audioAsset?.assetProxy.find((proxy) => proxy.mime === "audio/wav")?.key
      : "";
    this.projectReview.stereoFilePath =
      !!videoAsset && !audioAsset
        ? hasWav?.length > 0
          ? hasWav[0].file.name !==
            this.getExtractedAudioPath.substring(
              this.getExtractedAudioPath.lastIndexOf("/") + 1
            )
            ? hasWav[0].file.name
            : this.getExtractedAudioPath
          : this.getExtractedAudioPath
        : !!audioAsset
        ? audioPath
        : !!this.project?.stereoFilePath
        ? this.project.stereoFilePath
        : hasWav.length > 0
        ? hasWav[0].file.name
        : "";
    this.projectReview.uniqueProjectNames = this.projectId
      ? [this.project.uniqueProjectName]
      : this.projectReview.uniqueProjectNames;
  }

  get getExtractedAudioPath() {
    const videoAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "video"
    );
    return videoAsset?.assetProxy.find((proxy) => proxy.type === "audio")?.key;
  }
  // TODO UPDATE this portion to associated calculated lufs to each project
  // creating more than one project at once
  setProjectNamesAndFilePaths() {
    for (let i = 0; i < this.fileUploadContexts.length; i++) {
      const projectName =
        this.fileUploadContexts.length === 1
          ? this.formGroup.get("projectName").value
          : this.fileUploadContexts[i].projectName;
      this.projectReview.projectNames.push(projectName);
      this.projectReview.fileDetails.push({
        uniqueProjectName: this.fileUploadContexts[i].assetId,
        files: {
          ttmlFilePath: this.isScriptSourceType(this.getFileType(i))
            ? this.fileUploadContexts[i].file.name
            : null,
          stereoFilePath:
            this.getFileType(i) === "wav"
              ? this.fileUploadContexts[i].file.name
              : null,
          isAssetDropBox: false,
          lufs: this.fileUploadContexts[i].lufs,
        },
      });
      this.projectReview.uniqueProjectNames.push(
        this.fileUploadContexts[i].assetId
      );
    }
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    if (audioAsset) {
      const newId = newUuid();
      const projectName = audioAsset.fileName.split(".")[0];
      this.projectReview.projectNames.push(projectName);
      this.projectReview.fileDetails.push({
        uniqueProjectName: newId,
        files: {
          ttmlFilePath: null,
          stereoFilePath: audioAsset.fileName,
          isAssetDropBox: true,
        },
      });
      this.projectReview.uniqueProjectNames.push(newId);
    }
  }

  // single project scenario
  setProjectNameAndFilePathForSingleProject() {
    this.projectReview.projectNames.push(
      this.formGroup.get("projectName").value
    );
    if (this.fileUploadContexts.length === 1) {
      const audioAsset = this.selectedAsset?.find(
        (asset) => asset.assetType === "audio"
      );
      this.projectReview.fileDetails?.push({
        uniqueProjectName: this.fileUploadContexts[0].assetId,
        files: {
          ttmlFilePath: this.isScriptSourceType(this.getFileType(0))
            ? this.fileUploadContexts[0].file.name
            : null,
          stereoFilePath: !!this.selectedAsset
            ? this.getFileType(0) === "wav"
              ? this.fileUploadContexts[0].file.name !==
                this.getExtractedAudioPath?.substring(
                  this.getExtractedAudioPath?.lastIndexOf("/") + 1
                )
                ? this.fileUploadContexts[0].file.name
                : this.getExtractedAudioPath
              : this.getExtractedAudioPath
            : this.getFileType(0) === "wav"
            ? this.fileUploadContexts[0].file.name
            : null,
          isAssetDropBox: !!audioAsset,
        },
      });
    } else {
      const ttmlAttached = this.fileUploadContexts.filter((f) =>
        this.isScriptSourceType(f.file.name.split(".").reverse()[0])
      );
      const WavAttached = this.fileUploadContexts.filter(
        (f) => f.file.name.split(".").reverse()[0] === "wav"
      );
      const videoAsset = this.selectedAsset?.find(
        (asset) => asset.assetType === "video"
      );
      const audioAsset = this.selectedAsset?.find(
        (asset) => asset.assetType === "audio"
      );
      this.projectReview.fileDetails.push({
        uniqueProjectName: this.fileUploadContexts[0].assetId,
        files: {
          ttmlFilePath:
            ttmlAttached.length > 0 ? ttmlAttached[0].file.name : null,
          stereoFilePath: !!videoAsset
            ? WavAttached?.length > 0
              ? WavAttached[0].file.name !==
                this.getExtractedAudioPath.substring(
                  this.getExtractedAudioPath.lastIndexOf("/") + 1
                )
                ? WavAttached[0].file.name
                : this.getExtractedAudioPath
              : this.getExtractedAudioPath
            : WavAttached.length > 0
            ? WavAttached[0].file.name
            : null,
          isAssetDropBox: !!audioAsset || !!videoAsset,
        },
      });
    }
    this.projectReview.uniqueProjectNames.push(
      this.projectId
        ? this.project.uniqueProjectName
        : this.uniqueProjectName
        ? this.uniqueProjectName
        : this.fileUploadContexts[0].assetId
    );
  }

  get uniqueProjectName(): string {
    return this.formGroup.get("uniqueProjectName").value;
  }

  projectNameTooLong() {
    alert(this.translateService.instant("createProject.projectNameTooLong"));
    this.spinner = false;
    this.disableControl = false;
  }

  isProjectNameTooLong(projectDto: ProjectsDto): boolean {
    const projectsWithLongProjectNames = projectDto.projectNames.filter(
      (name) => name.length > environment.project.projectNameLength
    );
    return projectsWithLongProjectNames.length > 0;
  }

  setProjectStatus(projectsDto: ProjectsDto) {
    const scriptSourceAttached = this.fileUploadContexts.filter((file) =>
      this.isScriptSourceType(
        file.file.name.split(".").reverse()[0].toLowerCase()
      )
    );
    if (scriptSourceAttached.length > 0) {
      projectsDto.projectStatus = ProjectStatus.UploadingText;
    } else {
      projectsDto.projectStatus = ProjectStatus.WaitForTTML;
    }
  }

  public get isSelectedAssetDropBoxAudioOnly(): boolean {
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    return audioAsset ? audioAsset.assetType === "audio" : false;
  }

  public isOnlyWavUploaded() {
    const wavFilesUploadContext = this.fileUploadContexts.filter((files) => {
      return files.file.type.split("/")[1] === "wav" && !files.isDeleted;
    });
    const xmlAttached = this.fileUploadContexts.filter(
      (file) => file.file.name.split(".").reverse()[0] === "xml"
    );
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    const audioFileSelected = !!audioAsset;
    return (
      wavFilesUploadContext.length ===
        this.fileUploadContexts.filter((e) => !e.isDeleted).length ||
      (audioFileSelected && !xmlAttached.length)
    );
  }

  discardProject(discardAction: boolean): void {
    this.cancelledAssetIds = [];
    let dialogRef = this.dialog.open(DiscardProjectConfirmationComponent, {
      width: "775px",
      height: "450px",
      data: {
        discard: discardAction,
      },
    });

    dialogRef.afterClosed().subscribe((isCancellationConfirmed: boolean) => {
      if (isCancellationConfirmed) {
        if (discardAction) {
          this.confirmCancelUpload();
          this.projectService.discardFile(this.cancelledAssetIds).subscribe();
          this.router.navigateByUrl("/projects");
        } else {
          this.goToScriptWriting();
        }
      }
      dialogRef = undefined;
      return false;
    });
  }

  public processXmlFile(response: boolean, projectsDto: ProjectsDto) {
    if (response) {
      // No need to wait for response or for exception of ttmlXmlToOutputAudioFile because if any success or exception is handled and updating status in DB.
      const ttmlAttached = this.fileUploadContexts.filter(
        (file) => file.file.name.split(".").reverse()[0] === "xml"
      );
      if (ttmlAttached.length > 0 || projectsDto.replies?.length > 0) {
        this.textToSpeechService
          .convertTtmlIntoOutputAudioFile(projectsDto)
          .subscribe();
      }
    }
  }

  getFileType(index: number): string {
    return this.fileUploadContexts[index].file.name
      .split(".")
      .reverse()[0]
      .toLowerCase();
  }

  private isScriptSourceType(fileType: string): boolean {
    return ["pdf", "docx", "xml", "ttml"].includes(fileType.toLowerCase());
  }

  editProject() {
    this.setFormValues();
    setTimeout(() => {
      // dirty hack to override the ''
      this.formGroup.patchValue({
        voice: this.project.voice.split("/")[0],
      });
    }, 500);
    this.pageTitle = this.project.name;
    if (this.project.ttmlFilePath) {
      this.listEditProjectFiles(
        this.project.ttmlFilePath.substr(
          this.project.ttmlFilePath.lastIndexOf("/") + 1
        )
      );
    }
    if (
      this.project.stereoFilePath &&
      !this.canDisplayGeneratedAudioProxy &&
      !this.project.audioAssetId
    ) {
      this.listEditProjectFiles(
        this.project.stereoFilePath.substr(
          this.project.stereoFilePath.lastIndexOf("/") + 1
        )
      );
    }
    this.cdk.detectChanges();
    this.onLanguageSelectionChange(this.project.language, false);
    this.formGroup.patchValue({ lexicon: this.project.lexiconUri });
  }

  listEditProjectFiles(fileName: string) {
    const isXmlFile = fileName.toLowerCase().endsWith(".xml");
    const fileContext: FileUploadContext = {
      file: new File([""], fileName),
      assetId: this.project.uniqueProjectName,
      upload: {
        remainingTime: 0,
        status: Status.UploadSuccess,
        message: Status.UploadSuccess,
        progress: 100,
      },
      projectName: fileName,
      isValidated: true,
      retakesGenerationStoredInDb: isXmlFile ? true : undefined,
    };
    const existingContext = this.fileUploadContexts.find(
      (context) =>
        context.assetId === this.project.uniqueProjectName &&
        context.file.name === fileName
    );
    if (!existingContext) {
      this.fileUploadContexts.push(fileContext);
    }
  }

  get stereoFileName() {
    return this.project.stereoFilePath
      ? this.project.stereoFilePath.substr(
          this.project.stereoFilePath.lastIndexOf("/") + 1
        )
      : "";
  }

  setFormValues() {
    this.formGroup.patchValue({
      projectName: this.project.name,
      templateName: this.project.templateId,
      language: this.project.language,
      region: this.project.region,
      lexicon: this.project.lexiconUri,
      lufs: this.project.lufs
        ? Math.round(this.project.lufs).toString()
        : this.defaultLuf,
      startTimeCode: this.project.startTimeCode,
      frameRate: this.project.frameRate,
      speed: this.project.speedRate,
      automatedSpeedRate: this.project.automatedSpeedRate,
      isAutomatedSpeed: this.project.isAutomatedSpeedRate,
      isAutomatedMix: this.project.isAutomatedMix,
      usersToNotify: [],
      audioInputDevice: this.project.humanVoiceRecorderId,
      voiceSettingType: this.project.voiceSetting,
      voicerReadingSpeed: this.project.voicerReadingSpeed || 190,
      targetTranslationLanguage: this.project.targetTranslationLanguage,
      automatedSoundCorrection:
        this.project.automatedSoundCorrection === undefined
          ? true
          : this.project.automatedSoundCorrection,
      setTimeReferenceFromProject:
        this.project.setTimeReferenceFromProject === undefined
          ? false
          : this.project.setTimeReferenceFromProject,
      styleTransferEnabled:
        this.project.styleTransferEnabled === undefined
          ? false
          : this.project.styleTransferEnabled,
    });
  }

  public isFileUploadListValid(): boolean {
    return (
      this.fileUploadContexts.filter(
        (files) => !files.isDeleted && files.isValidated
      ).length > 0
    );
  }

  public undoDelete(file: FileUploadContext) {
    this.recentlyDeletedFile = null;
    this.undo(file);
  }

  // Generate Vd file and process mixing.
  generateVdFileAndProcessMix() {
    const isWavFileUploaded = this.fileUploadContexts.filter(
      (e) => e.isEdited && e.file.type.split("/")[1] === "wav"
    ).length;
    this.projectReview.stereoFilePath =
      this.projectReview.stereoFilePath?.includes("https")
        ? this.projectReview.stereoFilePath
        : !!isWavFileUploaded
        ? `${this.projectReview.uniqueProjectNames[0]}/${this.projectReview.fileDetails[0].files.stereoFilePath}`
        : "";

    this.projectReview.replies = this.project?.replies;
    this.textToSpeechService
      .generateVdFileAndProcessMix(this.projectReview)
      .subscribe();
  }

  // Only auphonic mixing.
  startMix() {
    const isWavFileUploaded = this.fileUploadContexts.filter(
      (e) => e.isEdited && e.file.type.split("/")[1] === "wav"
    );
    const automatedMix = {
      stereoTrackFileUrl: this.project.stereoFilePath,
      vDTrackFileUrl: this.project.vdFilePath,
      projectName: this.formGroup.get("projectName").value,
      uniqueProjectName: this.project.uniqueProjectName,
      fromUI: true,
    } as CreateAndStartProduction;

    if (!automatedMix.stereoTrackFileUrl || isWavFileUploaded.length > 0) {
      automatedMix.stereoTrackFileUrl = `${
        this.project.uniqueProjectName
      }/${encodeS3URI(this.projectReview.fileDetails[0].files.stereoFilePath)}`;
    }

    this.projectReview.stereoFilePath =
      this.projectReview.stereoFilePath?.includes("https")
        ? this.projectReview.stereoFilePath
        : !!isWavFileUploaded
        ? `${this.projectReview.uniqueProjectNames[0]}/${this.projectReview.fileDetails[0].files.stereoFilePath}`
        : "";

    this.projectReview.replies = this.project?.replies;

    this.textToSpeechService
      .generateVdFile(this.projectReview)
      .subscribe((vdFilePath) => {
        automatedMix.vDTrackFileUrl = vdFilePath;
        this.textToSpeechService.startMix(automatedMix).subscribe();
      });
  }

  public async updateProject(restartMix = false) {
    await this.buildReview();
    this.spinner = true;
    this.buildProject();
    if (this.isProjectNameTooLong(this.projectReview as ProjectsDto)) {
      this.projectNameTooLong();
      return;
    }
    this.spinner = true;
    this.projectReview.userId = this.project.userId;
    this.projectReview.id = this.project.id;
    this.projectReview.projectStatus = this.project.status;
    this.projectReview.audioFilePath = this.project.audioFilePath;

    let retakesToPerform = 0;

    for (let index = 0; index < this.project.replies.length; index++) {
      if (this.project.replies[index].isLongAudio) {
        retakesToPerform++;
      }
    }
    this.projectService.update(this.projectReview).subscribe(
      () => {
        if (
          this.project.status === ProjectStatus.ReadyToDownload &&
          (this.projectReview.isAutomatedMix || restartMix) &&
          this.project.startTimeCode !== this.projectReview.startTimeCode
        ) {
          this.generateVdFileAndProcessMix();
        } else if (
          this.project.status === ProjectStatus.ReadyToDownload &&
          (this.projectReview.isAutomatedMix || restartMix) &&
          retakesToPerform === 0 &&
          (this.project.stereoFilePath ||
            this.projectReview.fileDetails[0].files.stereoFilePath)
        ) {
          this.startMix();
        }

        this.spinner = false;
        this.router.navigateByUrl("/projects");
      },
      () => {
        this.disableControl = false; // enable continue button if there is any error.
        this.spinner = false;
      }
    );
  }

  public save(projectsDto: ProjectsDto, goToProjectListing?: boolean) {
    if (this.isProjectNameTooLong(projectsDto)) {
      this.projectNameTooLong();
      return;
    }
    this.spinner = true;
    this.setProjectStatus(projectsDto);

    this.projectService.save(projectsDto).subscribe(
      (data) => {
        this.formGroup.patchValue({
          uniqueProjectName: data.data.uniqueProjectName,
        });
        if (
          this.formGroup.get("voiceSettingType").value === VoiceType.Synthetic
        ) {
          this.processXmlFile(data.isSuccess, projectsDto);
        } else if (this.isVoicerSelected && projectsDto?.replies?.length) {
          this.generateVdFileAndProcessMix();
        }

        if (goToProjectListing) {
          this.router.navigateByUrl("/projects");
        }

        this.spinner = false;
      },
      (err) => {
        alert(err.error.message);
        this.disableControl = false; // enable save button if there is any error.
        this.spinner = false;
      }
    );
  }

  public restartProject(projectDto: ProjectsDto) {
    if (this.isProjectNameTooLong(projectDto)) {
      this.projectNameTooLong();
      return;
    }
    this.spinner = true;
    projectDto.userId = this.project.userId;
    projectDto.id = this.project.id;
    this.setProjectStatus(projectDto);
    this.projectService.update(projectDto).subscribe(
      (data) => {
        if (!data.isSuccess && data.data === "Non restartable status") {
          alert(
            this.translateService
              .instant("createProject.projectStatusError")
              .replace("{projectStatus}", data.status)
          );
          this.router.navigateByUrl("/projects");
          return;
        }
        if (
          this.formGroup.get("voiceSettingType").value === VoiceType.Synthetic
        ) {
          this.processXmlFile(data.isSuccess, projectDto);
        } else if (this.isVoicerSelected) {
          this.generateVdFileAndProcessMix();
        }
        this.router.navigateByUrl("/projects");
        this.spinner = false;
      },
      () => {
        this.disableControl = false; // enable save button if there is any error.
        this.spinner = false;
      }
    );
  }

  get isAllFilesAreXml(): boolean {
    return (
      !!this.fileUploadContexts?.length &&
      this.fileUploadContexts.every((upload) =>
        upload.file.name.includes(".xml")
      ) &&
      !this.selectedAsset
    );
  }

  get isAllFilesAreWav(): boolean {
    const xmlAttached = this.fileUploadContexts.filter(
      (file) => file.file.name.split(".").reverse()[0] === "xml"
    );
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    const videoAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "video"
    );
    const audioFileSelected = !!audioAsset;
    return (
      (!!this.fileUploadContexts?.length &&
        this.fileUploadContexts.every((upload) =>
          upload.file.name.includes(".wav")
        )) ||
      (audioFileSelected && !xmlAttached.length && !videoAsset)
    );
  }

  validateProjectFiles(): boolean {
    const xmlAttached = this.fileUploadContexts.filter(
      (file) => file.file.name.split(".").reverse()[0] === "xml"
    );
    const wavAttached = this.fileUploadContexts.filter(
      (file) => file.file.name.split(".").reverse()[0] === "wav"
    );
    const audioAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "audio"
    );
    const audioFileSelected = !!audioAsset;
    return (
      audioFileSelected && xmlAttached.length > 0 && wavAttached.length > 0
    );
  }

  async goToScriptWriting() {
    await this.buildReview();
    this.spinner = true;
    this.buildProject();
    if (this.isEmotionTransferSelected) {
      // For document workflows we skip script-writing and go directly back to the dashboard.
      this.save(this.projectReview, true);
      return;
    }
    const onlyXmlFileUploaded =
      this.fileUploadContexts?.length === 1 &&
      this.isXmlFileUploaded &&
      !this.selectedAsset;
    if (this.project) {
      this.project = { ...this.project, ...this.projectReview };
      this.saveProject();
      return;
    }
    if (
      !onlyXmlFileUploaded &&
      (this.isAllFilesAreWav || this.isAllFilesAreXml)
    ) {
      this.save(this.projectReview, true);
    } else if (onlyXmlFileUploaded) {
      this.saveProject();
    } else if (!this.project && !this.isXmlFileUploaded) {
      this.saveProject();
    } else {
      this.showProjectSection = false;
      this.spinner = false;
    }
  }

  public saveProject(): void {
    this.spinner = true;
    this.projectService.save(this.projectReview).subscribe((data) => {
      this.formGroup.patchValue({
        uniqueProjectName: data.data.uniqueProjectName,
      });
      this.getProjectByUniqueProjectName();
    });
  }

  public getProjectByUniqueProjectName(): void {
    this.projectService
      .getByUniqueProjectName(this.projectReview.uniqueProjectNames[0])
      .subscribe((result) => {
        this.project = result;
        this.projectId = result.id;
        this.spinner = false;
        this.showProjectSection = false;
      });
  }

  create() {
    this.buildReview();
    this.spinner = true;
    this.buildProject();
    this.projectReview.startVd = true;
    this.projectReview.replies = this.project?.replies || [];
    !this.projectReview.isRestarting
      ? this.save(this.projectReview, true)
      : this.restartProject(this.projectReview);
  }

  public getStatus(assetId: string, fileName: string): string | null {
    const fileUploaded = this.fileUploadContexts.find(
      (file) => file.file.name === fileName && file.assetId === assetId
    );
    return fileUploaded ? fileUploaded.upload.status : null;
  }

  // Returns time code in sec.
  private calculateTimeCodeInSec(timeCode: string, frameRate: number) {
    if (timeCode?.includes(".", 8)) {
      return getTimeInSecondsFromMillis(timeCode);
    } else if (timeCode?.includes(":", 8)) {
      return getTimeInSecondsFromFrameRate(timeCode, frameRate);
    } else {
      return 0;
    }
  }

  // Parse the ttml xml file and get the framerate and first tcIn of the segment from xml.
  public async parseTtmlAndCalculateSilence(ttmlString: string) {
    this.nonDropFileWarningMessage = "";
    const xMLDocument = new DOMParser().parseFromString(ttmlString, "text/xml");
    const frameRate = !this.formGroup.get("frameRate").value
      ? Number(
          xMLDocument
            .getElementsByTagName("tt")[0]
            ?.getAttribute("ttp:frameRate")
        )
      : this.formGroup.get("frameRate").value;
    const startTcIn = xMLDocument
      .getElementsByTagName("p")[0]
      ?.getAttribute("begin");
    frameRate !== 0
      ? this.formGroup.patchValue({ frameRate: frameRate })
      : this.formGroup.patchValue({ frameRate: "" });
    const firstTcInInSec = this.calculateTimeCodeInSec(startTcIn, frameRate);
    const startTimeCodeInSec = this.calculateTimeCodeInSec(
      this.formGroup.get("startTimeCode").value,
      frameRate
    );
    const dropMode = xMLDocument
      .getElementsByTagName("tt")[0]
      ?.getAttribute("ttp:dropMode");
    if (
      dropMode &&
      dropMode.toLowerCase().includes("nondrop") &&
      environment.project.nonDropFrameRates.includes(frameRate)
    ) {
      this.nonDropFileWarningMessage = this.translateService.instant(
        "createProject.nodDropFileWarning"
      );
    }
    return [firstTcInInSec, startTimeCodeInSec];
  }

  // Calculate the silence between start time code and first tcIn while editing project.
  private async calculateSilenceOnEdit() {
    if (this.project.replies) {
      const frameRate = this.formGroup.get("frameRate").value;
      const firstTcInInSec = this.calculateTimeCodeInSec(
        this.project.replies![0]?.tcIn,
        frameRate
      );
      const startTimeCodeInSec = this.calculateTimeCodeInSec(
        this.formGroup.get("startTimeCode").value,
        frameRate
      );
      this.setSilenceWarningMessage(firstTcInInSec, startTimeCodeInSec);
    }
  }

  // Calculate the silence between start time code and first tcIn while crating project.
  private async calculateSilenceOnCreate() {
    let ttmlString: string;
    const reader = new FileReader();
    const ttmlAttached = this.fileUploadContexts.filter(
      (file) => file.file.name.split(".").reverse()[0] === "xml"
    );

    if (ttmlAttached.length) {
      reader.readAsText(ttmlAttached[0].file, "utf8");
      reader.onloadend = async () => {
        ttmlString = reader.result as string;
        if (!!ttmlString) {
          ttmlString = reader.result as string;
          const [firstTcInInSec, startTimeCodeInSec] =
            await this.parseTtmlAndCalculateSilence(ttmlString);
          this.setSilenceWarningMessage(
            Number(firstTcInInSec),
            Number(startTimeCodeInSec)
          );
        }
      };
    }
  }

  // on change of start time code check the silence.
  public async onStartTimeCodeChange() {
    this.silenceWarningMessage = "";
    this.validateStartTimeCode();

    if (this.isEdit) {
      await this.calculateSilenceOnEdit();
    } else {
      await this.calculateSilenceOnCreate();
    }
  }

  setSilenceWarningMessage(firstTcInInSec: number, startTimeCodeInSec: number) {
    if (
      firstTcInInSec - startTimeCodeInSec >=
        environment.project.projectWarningSilence &&
      !this.project?.isGeneratedTtmlFile
    ) {
      this.silenceWarningMessage = this.translateService.instant(
        "createProject.silenceWarning"
      );
    } else if (
      startTimeCodeInSec > firstTcInInSec &&
      !this.project?.isGeneratedTtmlFile
    ) {
      this.silenceWarningMessage = this.translateService.instant(
        "createProject.startTimeCodeError"
      );
    }
  }

  public hideContinueButton() {
    const isXmlFileUploaded =
      this.fileUploadContexts.filter(
        (e) => e.isEdited && e.file.type.split("/")[1] === "xml"
      ).length > 0;
    return (
      isXmlFileUploaded ||
      !!this.project.errorMessage ||
      this.project.status === ProjectStatus.WaitForTTML
    );
  }

  public disableContinueAndRestart() {
    return (
      this.disableControl ||
      !!this.silenceWarningMessage ||
      !!this.nonDropFileWarningMessage ||
      !this.isAllFilesUploaded ||
      this.isNotEditable
    );
  }

  public getAllTemplates() {
    this.managementService.getAllTemplates().subscribe(
      (data) => {
        if (data) {
          const response = data
            .map((t) => {
              t.clientTemplateName = `${t.clientName}-${t.name}`;
              return t;
            })
            .sortBy("clientName");
          this.projectTemplate = this.backedProjectTemplate = response;
        } else {
          this.projectTemplate = this.backedProjectTemplate = [];
        }
      },
      () => {},
      () => {
        if (this.projectId) {
          this.selectedPresetTemplate = this.projectTemplate.filter(
            (template) => template.id === this.project.templateId
          )[0];
        }
      }
    );
  }

  initializeTemplate() {
    /* if input is something which does not give any result, onBlur should reassign the template,
    otherwise template list will be empty.
    -> in the template list it will be visible so there should be some delay.
    */
    setTimeout(() => {
      this.projectTemplate = this.backedProjectTemplate;
    }, 1000);
  }

  // Receive user input and send to search method**
  templateSearch(value: string) {
    this.projectTemplate = this.search(value);
  }

  // filter templates
  search(value: string) {
    const filterValue = value.toLowerCase().trim();
    if (filterValue === "") {
      return this.backedProjectTemplate;
    } else {
      return this.backedProjectTemplate.filter((t) =>
        t.clientTemplateName.toLowerCase().includes(filterValue)
      );
    }
  }

  usePresetTemplate(projectTemplate: ProjectTemplate) {
    this.onStartTimeCodeChange();

    this.selectedPresetTemplate = projectTemplate;
    this.formGroup.patchValue({
      language:
        this.formGroup.controls["language"].pristine ||
        (this.formGroup.controls["language"].dirty &&
          this.formGroup.controls["language"].untouched)
          ? projectTemplate.language
          : this.formGroup.get("language").value,
      region:
        this.formGroup.controls["region"].pristine ||
        (this.formGroup.controls["region"].dirty &&
          this.formGroup.controls["region"].untouched)
          ? projectTemplate.region
          : this.formGroup.get("region").value,
      voice:
        this.formGroup.controls["voice"].pristine ||
        (this.formGroup.controls["voice"].dirty &&
          this.formGroup.controls["voice"].untouched)
          ? projectTemplate.voice
          : this.formGroup.get("voice").value,
      startTimeCode:
        this.formGroup.controls["startTimeCode"].pristine ||
        (this.formGroup.controls["startTimeCode"].dirty &&
          this.formGroup.controls["startTimeCode"].untouched)
          ? projectTemplate.startTimeCode
          : this.formGroup.get("startTimeCode").value,
      speed:
        this.formGroup.controls["speed"].pristine ||
        (this.formGroup.controls["speed"].dirty &&
          this.formGroup.controls["speed"].untouched)
          ? projectTemplate.speedRate
          : this.formGroup.get("speed").value,
      automatedSpeedRate:
        this.formGroup.controls["automatedSpeedRate"].pristine ||
        (this.formGroup.controls["automatedSpeedRate"].dirty &&
          this.formGroup.controls["automatedSpeedRate"].untouched)
          ? projectTemplate.automatedSpeedRate
          : this.formGroup.get("automatedSpeedRate").value,
      isAutomatedSpeed:
        this.formGroup.controls["isAutomatedSpeed"].pristine ||
        (this.formGroup.controls["isAutomatedSpeed"].dirty &&
          this.formGroup.controls["isAutomatedSpeed"].untouched)
          ? projectTemplate.isAutomatedSpeedRate
          : this.formGroup.get("isAutomatedSpeed").value,
      isAutomatedMix:
        this.formGroup.controls["isAutomatedMix"].pristine ||
        (this.formGroup.controls["isAutomatedMix"].dirty &&
          this.formGroup.controls["isAutomatedMix"].untouched)
          ? projectTemplate.isAutomatedMix
          : this.formGroup.get("isAutomatedMix").value,
    });

    this.onLanguageSelectionChange(
      this.formGroup.get("language").value,
      true,
      projectTemplate.lexiconId
    );

    this.formGroup.patchValue({
      voice: projectTemplate.voice?.split("|")[1].trim() || "",
    });
  }

  automatedSpeedMessage() {
    let message = this.translateService.instant(
      "createProject.automatedSpeedMessage"
    );
    const maskedVoiceName =
      this.formGroup.get("voice").value && this.voices
        ? this.voices.find((voice) => {
            return voice.shortName === this.formGroup.get("voice").value;
          })
        : undefined;
    message = this.formGroup.get("voice").value
      ? this.translateService
          .instant("createProject.automatedSpeedMessage")
          .replace(
            "{voice}",
            maskedVoiceName ? maskedVoiceName?.maskedShortName : undefined
          )
      : message;
    message = message.replace("{speedRate}", this.formGroup.get("speed").value);
    message = message.replace(
      "{automatedSpeed}",
      this.formGroup.get("automatedSpeedRate").value
    );
    return message;
  }

  // When the user changes the voice or the lexicon, speed rate, automated variable speed or frame rate, user shouldn’t be able to click on “continue” which will not apply his changes but he can click on either Restart or back
  compareProjectData() {
    const currentProjectData: ProjectDataBackup = {
      voice: this.formGroup.get("voice").value,
      lexicon: this.formGroup.get("lexicon").value,
      speed: this.formGroup.get("speed").value,
      automatedSpeedRate: this.formGroup.get("automatedSpeedRate").value,
      frameRate: this.formGroup.get("frameRate").value,
      isAutomatedSpeed: this.formGroup.get("isAutomatedSpeed").value,
    };
    return (
      JSON.stringify(currentProjectData) !==
      JSON.stringify(this.projectDataBackup)
    );
  }

  isVoiceEmptyOrNull(): boolean {
    if (this.isVoicerSelected || this.isEmotionTransferSelected) {
      return false;
    }
    return (
      !this.voices ||
      this.formGroup.get("voice").value === "" ||
      this.isMatchingVoiceSelected()
    );
  }

  // to make sure that the correct voice from [lang-reg pair] is selected
  isMatchingVoiceSelected(): boolean {
    if (this.formGroup.get("voice").value !== "" && this.voices) {
      const voiceName = this.formGroup.get("voice").value;
      const voiceDetail = this.voices?.find((voice) => {
        return voice.shortName === voiceName;
      });
      return voiceDetail && voiceDetail?.maskedShortName ? false : true;
    } else {
      return true;
    }
  }

  public get isTtmlValid() {
    if (this.project && this.project.replies && this.project.replies.length) {
      return true;
    }

    if (this.isXmlFileUploaded) {
      const xmlUploads = this.fileUploadContexts.filter(
        (upload: FileUploadContext) =>
          !upload.isDeleted &&
          ((upload.file?.type && upload.file.type.includes("xml")) ||
            (upload.file?.name || "").toLowerCase().endsWith(".xml"))
      );

      const hasRetakesGenerated = xmlUploads.every(
        (u) => u.retakesGenerationStoredInDb === true
      );

      return hasRetakesGenerated;
    }
    return true;
  }

  public get noUploadFileSelected() {
    return this.fileUploadContexts.length === 0;
  }

  private isEmotionTransferValidationFailed(): boolean {
    if (!this.isEmotionTransferSelected) {
      return false;
    }

    const hasDocumentFile = this.unRemovedFileUploadContexts.some(
      (fileUpload) => {
        const ext = fileUpload.file.name.split(".").pop()?.toLowerCase() || "";
        return this.isScriptSourceType(ext);
      }
    );

    const hasVideoAssetFromDropbox = this.selectedAsset?.some(
      (asset) => asset.assetType === "video"
    );

    const hasAudioAssetFromDropbox = this.selectedAsset?.some(
      (asset) => asset.assetType === "audio"
    );

    const hasAudioAssetUploaded = this.unRemovedFileUploadContexts.some(
      (fileUpload) => {
        const extension =
          "." + fileUpload.file.name.split(".").pop().toLowerCase();
        return extension === ".wav";
      }
    );

    return (
      !hasDocumentFile ||
      !(
        hasVideoAssetFromDropbox ||
        hasAudioAssetFromDropbox ||
        hasAudioAssetUploaded
      )
    );
  }

  get disableSettingsNextBtn(): boolean {
    return (
      this.validateProjectFiles() ||
      this.isEmotionTransferValidationFailed() ||
      (this.noUploadFileSelected &&
        !this.canDisplaySelectedAssetsFromDropbox) || // at least one file must have been selected, either WAV or TTML
      !this.isTtmlValid || // at least 1 file selected for upload -> in case of TTML, let's check if it's valid
      !this.formGroup.valid ||
      !!this.silenceWarningMessage ||
      !!this.nonDropFileWarningMessage ||
      this.isVoiceEmptyOrNull() ||
      this.spinner ||
      !this.uploadSuccessStatus ||
      (environment.project.nonEditableStatus.includes(
        this.project?.status as ProjectStatus
      ) &&
        !this.project?.errorMessage)
    );
  }

  get uploadSuccessStatus(): boolean {
    return this.unRemovedFileUploadContexts.length
      ? this.unRemovedFileUploadContexts.every((fileUploadContext) => {
          return fileUploadContext.upload.status == this.status.UploadSuccess;
        })
      : true;
  }

  get xmlFileUploadContexts() {
    return this.fileUploadContexts.filter(
      (fileUpload) =>
        fileUpload.file.name.includes(".xml") && !fileUpload.isDeleted
    );
  }

  get canDisplaySelectedAssetsFromDropbox(): boolean {
    return !!this.selectedAsset;
  }

  get canDisplayGeneratedAudioProxy(): boolean {
    const videoAsset = this.selectedAsset?.find(
      (asset) => asset.assetType === "video"
    );
    const proxyIndex = videoAsset?.assetProxy?.findIndex(
      (proxy) => proxy.key === this.project?.stereoFilePath
    );
    const audioIndex = this.selectedAsset?.findIndex(
      (asset) => asset.assetType === "audio"
    );

    return !!this.selectedAsset && audioIndex < 0 && proxyIndex >= 0;
  }

  get selectedVideoAsset(): Asset {
    return this.selectedAsset?.find((asset) => asset.assetType === "video");
  }

  public removeSelectedVideoAsset(index: number): void {
    if (
      this.project?.stereoFilePath &&
      this.selectedAsset[index].assetProxy.some(
        (proxy) => proxy.key === this.project?.stereoFilePath
      )
    ) {
      this.project.stereoFilePath = null;
    }

    if (
      this.project?.videoAssetId &&
      this.selectedAsset[index].assetType === "video"
    ) {
      this.project.videoAssetId = null;
    }

    if (
      this.project?.audioAssetId &&
      this.selectedAsset[index].assetType === "audio"
    ) {
      this.project.audioAssetId = null;
    }

    this.projectReview = { ...this.projectReview, ...this.project };
    if (
      this.selectedAsset[index].assetProxy.some(
        (proxy) => proxy.key === this.project?.stereoFilePath
      )
    ) {
      this.projectReview.stereoFilePath = null;
    }

    if (this.selectedAsset[index].assetType === "video") {
      this.projectReview.videoAssetId = null;
    }

    if (this.selectedAsset[index].assetType === "audio") {
      this.projectReview.audioAssetId = null;
    }

    sessionStorage.setItem(
      "draftProjectData",
      JSON.stringify({
        stereoFilePath: this.projectReview.stereoFilePath,
        videoAssetId: this.projectReview.videoAssetId,
        audioAssetId: this.projectReview.audioAssetId,
        ...this.formGroup.value,
      })
    );

    this.selectedAsset?.splice(index, 1);
    this.assetSelectionService.removeSelection();
    this.assetSelectionService.setSelection({
      assets: this.selectedAsset,
    });
    if (!!this.projectReview?.id) {
      const projectAssets = {
        id: this.projectReview.id,
        stereoFilePath: this.projectReview.stereoFilePath,
        videoAssetId: this.projectReview.videoAssetId,
        audioAssetId: this.projectReview.audioAssetId,
      };
      this.spinner = true;
      this.projectService.updateProjectAssets(projectAssets).subscribe(
        () => {
          this.spinner = false;
        },
        (err) => {
          alert(err.error.message);
          this.disableControl = false; // enable save button if there is any error.
          this.spinner = false;
        }
      );
    }
  }

  public updateProjectFromScript(project: Project) {
    this.project = project;
    this.cdk.detectChanges();
  }

  public get uploadMessage() {
    const msg = this.isEmotionTransferSelected
      ? "createProject.uploadSectionEmotionTransfer"
      : "createProject.uploadSection";
    return this.translateService.instant(msg);
  }
}
